import React, { useState, useEffect } from "react";
import { FaAngleRight } from "react-icons/fa6";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { MdFileDownload } from "react-icons/md";
import { BiTrendingUp, } from "react-icons/bi";
import { HiOutlineFilter } from "react-icons/hi";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { 
  getAllRestaurantReports,
  setTimeRange,
  setSelectedRestaurant,
  clearErrors,
} from "../../../redux/restaurantReports/restaurantReportsSlice";
import { 
  DailyOrderChart, 
  OrderCategoryChart, 
  RevenueChart, 
  MetricCard 
} from "./components/Charts";
import {
  formatCurrency,
  formatNumber,
  convertToMockDailyOrderData,
  convertToMockRevenueData,
  convertToMockCategoryData,
  createRestaurantFilterOptions,
  timeFilterOptions,
  getDateRangeText,
} from "./dataTransforms";
import LoadingSpinner from "../../../components/elements/LoadingSpinner";
import SearchableSelect from "./components/SearchableSelect";

const Index = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    allRestaurantsReport,
    allRestaurantsLoading,
    allRestaurantsError,
    selectedTimeRange,
    selectedRestaurant,
  } = useAppSelector((state) => state.restaurantReports);
  
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Load data on component mount and filter changes
  useEffect(() => {
    dispatch(getAllRestaurantReports({ timeRange: selectedTimeRange }));
  }, [dispatch, selectedTimeRange]);

  // Clear errors on component mount
  useEffect(() => {
    dispatch(clearErrors());
  }, [dispatch]);

  // Handle filter changes
  const handleTimeFilterChange = (value: string) => {
    dispatch(setTimeRange(value));
  };

  const handleRestaurantFilterChange = (value: string) => {
    dispatch(setSelectedRestaurant(value));
  };

  const handleExport = (format: 'pdf' | 'excel' | 'csv') => {
    // Simulate export functionality
    console.log(`Exporting report as ${format}`);
    setShowExportMenu(false);
    // In real implementation, this would trigger the actual export
  };

  // Prepare data for charts
  const combinedSummary = allRestaurantsReport?.combinedSummary;
  const combinedMetrics = allRestaurantsReport?.combinedMetrics;
  const restaurantReports = allRestaurantsReport?.restaurantReports || [];
  
  // Determine which data to use based on selected restaurant
  const isAllRestaurantsSelected = selectedRestaurant === "all";
  const selectedRestaurantData = restaurantReports.find(r => r.restaurantId === selectedRestaurant);
  
  // Get metrics data based on selection
  const metricsData = isAllRestaurantsSelected 
    ? combinedMetrics 
    : selectedRestaurantData;
    
  // Get summary data based on selection
  const summaryData = isAllRestaurantsSelected 
    ? combinedSummary 
    : selectedRestaurantData 
      ? {
          totalRestaurants: 1,
          totalOrders: selectedRestaurantData.totalOrders,
          totalRevenue: selectedRestaurantData.totalRevenue,
          averageDailyOrders: Math.round(selectedRestaurantData.totalOrders / 30) // Estimate based on 30 days
        } 
      : null;
  
  // Transform data for existing chart components
  const dailyOrderData = metricsData?.dailyOrderVolume 
    ? convertToMockDailyOrderData(metricsData.dailyOrderVolume) : [];
  const revenueData = metricsData?.weeklyRevenue 
    ? convertToMockRevenueData(metricsData.weeklyRevenue) : [];
  const categoryData = metricsData?.categoryBreakdown 
    ? convertToMockCategoryData(metricsData.categoryBreakdown) : [];
  
  // Debug logging to help identify chart data issues
  React.useEffect(() => {
    console.log('Selected Restaurant:', selectedRestaurant);
    console.log('Is All Restaurants Selected:', isAllRestaurantsSelected);
    console.log('Selected Restaurant Data:', selectedRestaurantData);
    console.log('Metrics Data:', metricsData);
    console.log('Summary Data:', summaryData);
    console.log('Daily Order Data:', dailyOrderData);
    console.log('Revenue Data:', revenueData);
    console.log('Category Data:', categoryData);
  }, [selectedRestaurant, isAllRestaurantsSelected, selectedRestaurantData, metricsData, summaryData, dailyOrderData, revenueData, categoryData]);
  
  // Create restaurant filter options
  const restaurantFilterOptions = createRestaurantFilterOptions(restaurantReports);
  
  // Get selected filters for display
  const selectedTimeFilter = timeFilterOptions.find(f => f.value === selectedTimeRange);
  const selectedRestaurantFilter = restaurantFilterOptions.find(f => f.value === selectedRestaurant);

  if (allRestaurantsLoading) {
    return (
      <section className="font-mont">
        <div className="flex items-start gap-4">
          <button
            className="cursor-pointer p-2 rounded-full bg-[#008B5014]"
            onClick={() => navigate(-1)}
          >
            <MdOutlineKeyboardBackspace className="w-6 h-6" />
          </button>
          <article>
            <h1 className="text-2xl font-medium mb-2 inline-flex items-center gap-3">
              <span className="font-semibold">Restaurant</span> <FaAngleRight />
              <span className="font-semibold">Reports and Analytics</span>
            </h1>
            <p className="text-sm">
              Generate comprehensive reports and analyze restaurant performance metrics.
            </p>
          </article>
        </div>
        <div className="flex items-center justify-center h-64 bg-white rounded-md my-5">
          <LoadingSpinner />
        </div>
      </section>
    );
  }

  return (
    <section className="font-mont bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white p-6">
        <div className="flex items-start gap-4">
          <button
            className="cursor-pointer p-2 rounded-full bg-[#008B5014]"
            onClick={() => navigate(-1)}
          >
            <MdOutlineKeyboardBackspace className="w-6 h-6" />
          </button>
          <article className="flex-1">
            <h1 className="text-2xl font-medium mb-2 inline-flex items-center gap-3">
              <span className="font-semibold">Restaurant</span> <FaAngleRight />
              <span className="font-semibold">Reports and Analytics</span>
            </h1>
            <p className="text-sm">
              Generate comprehensive reports and analyze restaurant performance metrics.
            </p>
          </article>
          
          {/* Export Button */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-dark transition-colors"
            >
              <MdFileDownload className="w-4 h-4" />
              Export Report
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border z-10">
                <div className="py-1">
                  <button
                    onClick={() => handleExport('pdf')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Export as PDF
                  </button>
                  <button
                    onClick={() => handleExport('excel')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Export as Excel
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Export as CSV
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <HiOutlineFilter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <select
            value={selectedTimeRange}
            onChange={(e) => handleTimeFilterChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
          >
            {timeFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <SearchableSelect
            value={selectedRestaurant}
            onChange={handleRestaurantFilterChange}
            options={restaurantFilterOptions}
            placeholder="Search restaurants..."
            className="min-w-64"
          />
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Error Display */}
        {allRestaurantsError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <p>Error: {allRestaurantsError}</p>
            <button 
              onClick={() => dispatch(getAllRestaurantReports({ timeRange: selectedTimeRange }))}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Report Period Display */}
        {allRestaurantsReport?.reportPeriod && (
          <div className="bg-blue-50 border border-blue-200 px-4 py-3 rounded">
            <p className="text-sm text-blue-800">
              <strong>Report Period:</strong> {getDateRangeText(
                allRestaurantsReport.reportPeriod.startDate,
                allRestaurantsReport.reportPeriod.endDate
              )}
            </p>
            {!isAllRestaurantsSelected && selectedRestaurantData && (
              <p className="text-sm text-blue-800 mt-1">
                <strong>Showing data for:</strong> {selectedRestaurantData.restaurantName}
              </p>
            )}
          </div>
        )}
        
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Orders"
            value={formatNumber(summaryData?.totalOrders || 0)}
            color="green"
          />
          
          <MetricCard
            title="Total Revenue"
            value={formatCurrency(summaryData?.totalRevenue || 0)}
            color="blue"
          />
          
          <MetricCard
            title={isAllRestaurantsSelected ? "Total Restaurants" : "Restaurant"}
            value={isAllRestaurantsSelected 
              ? formatNumber(summaryData?.totalRestaurants || 0)
              : selectedRestaurantData?.restaurantName || "N/A"}
            color="purple"
          />
          
          <MetricCard
            title="Avg Daily Orders"
            value={formatNumber(summaryData?.averageDailyOrders || 0)}
            color="orange"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Order Volume Chart */}
          {dailyOrderData.length > 0 ? (
            <DailyOrderChart data={dailyOrderData} height={400} />
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Daily Order Volume</h3>
              <div className="flex items-center justify-center h-64 text-gray-500">
                No data available for the selected period
              </div>
            </div>
          )}
          
          {/* Order Categories Chart */}
          {categoryData.length > 0 ? (
            <OrderCategoryChart data={categoryData} height={400} />
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Order Categories</h3>
              <div className="flex items-center justify-center h-64 text-gray-500">
                No category data available
              </div>
            </div>
          )}
        </div>

        {/* Revenue Trend Chart */}
        <div className="w-full">
          {revenueData.length > 0 ? (
            <RevenueChart data={revenueData} height={400} />
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Revenue Trend</h3>
              <div className="flex items-center justify-center h-64 text-gray-500">
                No revenue data available for the selected period
              </div>
            </div>
          )}
        </div>
        
        {/* Restaurant Performance Table */}
        {(() => {
          // Filter restaurants based on selection
          const filteredRestaurants = isAllRestaurantsSelected 
            ? restaurantReports 
            : selectedRestaurantData 
              ? [selectedRestaurantData] 
              : [];
              
          return filteredRestaurants.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold">
                  {isAllRestaurantsSelected ? "Restaurant Performance" : `${selectedRestaurantData?.restaurantName || "Restaurant"} Performance`}
                </h3>
                <p className="text-sm text-gray-600">
                  {isAllRestaurantsSelected 
                    ? "Individual restaurant metrics and performance" 
                    : "Detailed metrics for selected restaurant"}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Restaurant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Orders
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Revenue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Avg Order Value
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRestaurants.map((restaurant) => {
                      const avgOrderValue = restaurant.totalOrders > 0 
                        ? restaurant.totalRevenue / restaurant.totalOrders 
                        : 0;
                      
                      return (
                        <tr key={restaurant.restaurantId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {restaurant.restaurantName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(restaurant.totalOrders)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(restaurant.totalRevenue)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(avgOrderValue)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>
      
      {/* Close export menu when clicking outside */}
      {showExportMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowExportMenu(false)}
        />
      )}
    </section>
  );
};

export default Index;